import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { addPartnerOffer } from '@/lib/firebase/queries/offers';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface AddOfferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddOfferDialog({ open, onOpenChange, onSuccess }: AddOfferDialogProps) {
  const { t, dir } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    pointsRequired: '',
    discountPercentage: '',
    imageUrl: '',
    validUntil: new Date(),
    terms: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.pointsRequired || !form.discountPercentage || !form.imageUrl) {
      toast.error(t('dashboard.partner.dashboard.offers.form.error'));
      return;
    }

    try {
      setLoading(true);
      await addPartnerOffer({
        ...form,
        pointsRequired: Number(form.pointsRequired),
        discountPercentage: Number(form.discountPercentage),
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date()
      });
      toast.success(t('dashboard.partner.dashboard.offers.form.success'));
      onSuccess();
      onOpenChange(false);
      setForm({
        title: '',
        description: '',
        pointsRequired: '',
        discountPercentage: '',
        imageUrl: '',
        validUntil: new Date(),
        terms: ''
      });
    } catch (error) {
      console.error('Error adding offer:', error);
      toast.error(t('dashboard.partner.dashboard.offers.form.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-4xl ${dir === 'rtl' ? 'text-right' : 'text-left'}`} dir={dir}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{t('dashboard.partner.dashboard.offers.add')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-base">{t('dashboard.partner.dashboard.offers.form.title')}</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder={t('dashboard.partner.dashboard.offers.form.title')}
                  dir={dir}
                  className="h-10"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-base">{t('dashboard.partner.dashboard.offers.form.description')}</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={t('dashboard.partner.dashboard.offers.form.description')}
                  dir={dir}
                  className="min-h-[100px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="points" className="text-base">{t('dashboard.partner.dashboard.offers.form.points')}</Label>
                  <Input
                    id="points"
                    type="number"
                    value={form.pointsRequired}
                    onChange={(e) => setForm({ ...form, pointsRequired: e.target.value })}
                    placeholder={t('dashboard.partner.dashboard.offers.form.points')}
                    dir={dir}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="discount" className="text-base">{t('dashboard.partner.dashboard.offers.form.discount')}</Label>
                  <Input
                    id="discount"
                    type="number"
                    min="0"
                    max="100"
                    value={form.discountPercentage}
                    onChange={(e) => setForm({ ...form, discountPercentage: e.target.value })}
                    placeholder={t('dashboard.partner.dashboard.offers.form.discount')}
                    dir={dir}
                    className="h-10"
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="imageUrl" className="text-base">{t('dashboard.partner.dashboard.offers.form.imageUrl')}</Label>
                <Input
                  id="imageUrl"
                  type="url"
                  value={form.imageUrl}
                  onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  placeholder={t('dashboard.partner.dashboard.offers.form.imageUrl')}
                  dir={dir}
                  className="h-10"
                />
                {form.imageUrl && (
                  <div className="mt-2">
                    <img
                      src={form.imageUrl}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg border"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/150';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-base">{t('dashboard.partner.dashboard.offers.form.validUntil')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal h-10',
                        !form.validUntil && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.validUntil ? format(form.validUntil, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={form.validUntil}
                      onSelect={(date) => date && setForm({ ...form, validUntil: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="terms" className="text-base">{t('dashboard.partner.dashboard.offers.form.terms')}</Label>
                <Textarea
                  id="terms"
                  value={form.terms}
                  onChange={(e) => setForm({ ...form, terms: e.target.value })}
                  placeholder={t('dashboard.partner.dashboard.offers.form.terms')}
                  dir={dir}
                  className="min-h-[100px]"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('dashboard.partner.dashboard.offers.form.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('dashboard.partner.dashboard.offers.form.saving') : t('dashboard.partner.dashboard.offers.form.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 